<template>
  <el-dialog
    v-model="visible"
    title="编辑门店信息"
    width="60%"
    :close-on-click-modal="false"
    @closed="onClosed"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-width="140px">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="门店名称" prop="storeName">
            <el-input v-model="form.storeName" placeholder="请输入门店名称" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="门店编码" prop="storeEntCode">
            <el-input v-model="form.storeEntCode" disabled placeholder="门店编码不可修改" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="开店模式" prop="storePattern">
            <el-select v-model="form.storePattern" placeholder="请选择开店模式">
              <el-option label="直营" value="direct" />
              <el-option label="加盟" value="franchise" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="租期时长(年)" prop="rentTerm">
            <el-input v-model.number="form.rentTerm" type="number" placeholder="请输入租期时长" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="所在省份" prop="province">
            <el-input v-model="form.province" placeholder="请输入所在省份" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="所在城市" prop="city">
            <el-input v-model="form.city" placeholder="请输入所在城市" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="开店投资金额" prop="investAmountForOpening">
            <el-input v-model.number="form.investAmountForOpening" type="number" placeholder="请输入开店投资金额" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="租期开始时间" prop="groundLeaseTermBegin">
            <el-date-picker v-model="form.groundLeaseTermBegin" type="date" placeholder="选择租期开始时间" value-format="YYYY-MM-DD" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="租期结束时间" prop="groundLeaseTermEnd">
            <el-date-picker v-model="form.groundLeaseTermEnd" type="date" placeholder="选择租期结束时间" value-format="YYYY-MM-DD" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <el-form-item label="详细地址" prop="entDetailAddress">
            <el-input v-model="form.entDetailAddress" type="textarea" :rows="3" placeholder="请输入详细地址" />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <template #footer>
      <button class="btn" @click="visible = false">取消</button>
      <button class="btn btn-primary" :disabled="loading" @click="submit">
        {{ loading ? '提交中…' : '提交' }}
      </button>
    </template>
  </el-dialog>
</template>

<script>
import { ref, reactive, computed, watch, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import assetService from '@/services/assetService'

const toDateStr = (v) => {
  if (!v) return ''
  try {
    return new Date(v).toISOString().split('T')[0]
  } catch {
    return ''
  }
}

export default {
  name: 'StoreEditDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    storeInfo: { type: Object, default: () => ({}) },
    storeEntCode: { type: String, default: '' }
  },
  emits: ['update:modelValue', 'saved'],
  setup(props, { emit }) {
    const formRef = ref(null)
    const loading = ref(false)

    const form = reactive({
      storeName: '',
      storeEntCode: '',
      storePattern: '',
      province: '',
      city: '',
      rentTerm: null,
      investAmountForOpening: null,
      groundLeaseTermBegin: '',
      groundLeaseTermEnd: '',
      entDetailAddress: ''
    })

    const rules = {
      storeName: [{ required: true, message: '请输入门店名称', trigger: 'blur' }],
      storeEntCode: [{ required: true, message: '门店编码不能为空', trigger: 'blur' }],
      storePattern: [{ required: true, message: '请选择开店模式', trigger: 'change' }],
      province: [{ required: true, message: '请输入所在省份', trigger: 'blur' }],
      city: [{ required: true, message: '请输入所在城市', trigger: 'blur' }],
      groundLeaseTermBegin: [{ required: true, message: '请选择租期开始时间', trigger: 'change' }],
      groundLeaseTermEnd: [{ required: true, message: '请选择租期结束时间', trigger: 'change' }],
      entDetailAddress: [{ required: true, message: '请输入详细地址', trigger: 'blur' }]
    }

    const visible = computed({
      get: () => props.modelValue,
      set: (v) => emit('update:modelValue', v)
    })

    // 打开时用当前门店信息回填
    watch(() => props.modelValue, async (open) => {
      if (!open) return
      const s = props.storeInfo || {}
      form.storeName = s.storeName || ''
      form.storeEntCode = s.storeEntCode || props.storeEntCode || ''
      form.storePattern = s.storePattern || ''
      form.province = s.province || ''
      form.city = s.city || ''
      form.rentTerm = s.rentTerm ?? null
      form.investAmountForOpening = s.investAmountForOpening ?? null
      form.groundLeaseTermBegin = toDateStr(s.groundLeaseTermBegin)
      form.groundLeaseTermEnd = toDateStr(s.groundLeaseTermEnd)
      form.entDetailAddress = s.entDetailAddress || ''
      await nextTick()
      formRef.value?.clearValidate()
    })

    const onClosed = () => {
      formRef.value?.clearValidate()
    }

    const submit = async () => {
      if (!formRef.value) return
      try {
        await formRef.value.validate()
      } catch {
        return // 校验失败不提示
      }

      loading.value = true
      try {
        const response = await assetService.updateStoreInfo({
          ...form,
          storeEntCode: props.storeEntCode || form.storeEntCode
        })
        if (response?.success) {
          ElMessage.success('门店信息更新成功')
          visible.value = false
          emit('saved')
        } else {
          ElMessage.error('门店信息更新失败：' + (response?.message || '未知错误'))
        }
      } catch (error) {
        ElMessage.error('提交失败：' + (error?.message || ''))
      } finally {
        loading.value = false
      }
    }

    return { formRef, form, rules, visible, loading, submit, onClosed }
  }
}
</script>

<style scoped>
.btn {
  padding: 6px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover:not(:disabled) {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: var(--brand-600);
  border-color: var(--brand-600);
  color: #fff;
}
</style>
